import React from "react";
import { Download, Printer, Copy, Check, Info } from "lucide-react";

/* ---------- DATA ----------------------------------------------------- */

const COLORS = [
    {
        name: "Coral",
        role: "Primary CTAs, accent",
        hex: "#FF5A5F",
        rgb: "255, 90, 95",
        text: "#FFFFFF",
    },
    {
        name: "Lime",
        role: "Secondary accents, success",
        hex: "#A3E635",
        rgb: "163, 230, 53",
        text: "#365314",
    },
    {
        name: "Lime Ink",
        role: "Lime text, dark accents on lime",
        hex: "#365314",
        rgb: "54, 83, 20",
        text: "#FFFFFF",
    },
    {
        name: "Slate 900",
        role: "Headlines, dark surfaces",
        hex: "#0F172A",
        rgb: "15, 23, 42",
        text: "#FFFFFF",
    },
    {
        name: "Slate 600",
        role: "Body copy",
        hex: "#475569",
        rgb: "71, 85, 105",
        text: "#FFFFFF",
    },
    {
        name: "Slate 400",
        role: "Subtle text, captions",
        hex: "#94A3B8",
        rgb: "148, 163, 184",
        text: "#FFFFFF",
    },
    {
        name: "Warm Surface",
        role: "Section bg (off-white)",
        hex: "#FAF8F2",
        rgb: "250, 248, 242",
        text: "#0F172A",
    },
    {
        name: "Hairline",
        role: "Borders, dividers",
        hex: "#EAEAEA",
        rgb: "234, 234, 234",
        text: "#0F172A",
    },
];

const TYPESCALE = [
    { tag: "H1", spec: "Bricolage Grotesque · 700 · 60–96px / 0.95", sample: "Make it once. Get paid every time." },
    { tag: "H2", spec: "Bricolage Grotesque · 600 · 36–48px / 1.05", sample: "Stop making more content." },
    { tag: "H3", spec: "Bricolage Grotesque · 600 · 20–24px / 1.2", sample: "Turn Content Into Ads" },
    { tag: "Body", spec: "Plus Jakarta Sans · 400/500 · 16–18px / 1.6", sample: "Castline turns creator videos into reusable ad inventory." },
    { tag: "Small", spec: "Plus Jakarta Sans · 500 · 12–14px", sample: "Per-channel attribution and creator-payout reporting" },
    { tag: "Overline", spec: "Plus Jakarta Sans · 600 · 10–11px · 0.1em tracking · UPPERCASE", sample: "FOR BRANDS" },
];

const VOICE = [
    { do: "Direct", dont: "Hyped" },
    { do: "Concrete numbers", dont: "Vague claims" },
    { do: "\u201CMake it once. Get paid every time.\u201D", dont: "\u201CRevolutionising the creator economy\u201D" },
    { do: "Lower-case product name (castline)", dont: "ALL-CAPS or Title Case" },
    { do: "Operator-friendly (ROAS, attribution, channels)", dont: "Buzzwords (synergy, disrupt)" },
];

const KEY_LINES = [
    {
        label: "Tagline",
        value: "Make it once. Get paid every time it performs.",
    },
    {
        label: "One-liner",
        value: "Castline turns creator videos into reusable ad inventory. Brands run it across Meta, Google, Amazon, YouTube and more — creators earn an ongoing commission on the revenue it drives.",
    },
    {
        label: "Brand pitch (one sentence)",
        value: "Scale winning creator content across every channel.",
    },
    {
        label: "Creator pitch (one sentence)",
        value: "Post once. Earn everywhere it performs.",
    },
    {
        label: "Category",
        value: "Content-as-ad-inventory.",
    },
];

/* ---------- HELPERS -------------------------------------------------- */

function CopyChip({ text, label }) {
    const [copied, setCopied] = React.useState(false);
    const onCopy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1400);
        } catch {
            // ignore
        }
    };
    return (
        <button
            onClick={onCopy}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-900 transition-colors"
            data-testid={`copy-${label}`}
        >
            {copied ? (
                <Check className="w-3.5 h-3.5 text-lime-ink" />
            ) : (
                <Copy className="w-3.5 h-3.5" />
            )}
            {copied ? "Copied" : "Copy"}
        </button>
    );
}

function DownloadButton({ href, label }) {
    return (
        <a
            href={href}
            download
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-coral transition-colors"
            data-testid={`download-${label}`}
        >
            <Download className="w-3.5 h-3.5" />
            {label}
        </a>
    );
}

/* ---------- PAGE ----------------------------------------------------- */

export default function BrandBook() {
    return (
        <div className="min-h-screen bg-white text-slate-900 brand-book">
            {/* HEADER STRIP */}
            <header className="border-b hairline bg-white sticky top-0 z-40 print:hidden">
                <div className="max-w-[960px] mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
                    <a href="/" className="flex items-center gap-2" data-testid="bb-home">
                        <img src="/brand/logo-mark.svg" alt="Castline" className="w-8 h-8" />
                        <span className="font-display text-xl font-semibold">castline</span>
                        <span className="text-slate-300 mx-1">·</span>
                        <span className="text-sm text-slate-500">brand book</span>
                    </a>
                    <button
                        onClick={() => window.print()}
                        className="btn-pill-primary inline-flex items-center"
                        data-testid="bb-print"
                    >
                        <Printer className="w-4 h-4 mr-2" /> Download as PDF
                    </button>
                </div>
            </header>

            <main className="max-w-[960px] mx-auto px-5 lg:px-8 py-12 lg:py-16 space-y-16">
                {/* COVER */}
                <section data-testid="bb-cover">
                    <div className="chip chip-coral mb-5 inline-flex">CASTLINE BRAND BOOK · v1</div>
                    <h1 className="font-display text-5xl md:text-6xl font-semibold leading-[1] mb-5">
                        Make it once.
                        <br />
                        <span className="relative inline-block">
                            <span className="relative z-10">Get paid every time it performs.</span>
                            <span className="absolute inset-x-0 bottom-1 h-3 bg-lime/70 -z-0 rounded-sm" />
                        </span>
                    </h1>
                    <p className="text-slate-600 text-lg max-w-2xl leading-relaxed mb-6">
                        A short, working guide to how Castline looks, sounds and shows up — for emails,
                        decks, launch posts and partner comms. Last updated: April 2026.
                    </p>
                    <div className="card-ecom surface-warm p-4 flex items-start gap-3 max-w-2xl print:hidden" data-testid="bb-howto">
                        <Info className="w-4 h-4 text-coral flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-slate-700 leading-relaxed">
                            <strong>Need a PDF copy?</strong> Hit the coral{" "}
                            <span className="font-semibold">Download as PDF</span> button up top — your browser will open the print dialog, choose <em>Save as PDF</em> as the destination, then Save. Logos and key marketing lines stay copyable inline below.
                        </div>
                    </div>
                </section>

                {/* LOGO ----------------------------------------------- */}
                <section data-testid="bb-logo">
                    <div className="overline text-coral mb-2">01 · LOGO</div>
                    <h2 className="font-display text-3xl font-semibold tracking-tight mb-1">
                        Logo &amp; lockups
                    </h2>
                    <p className="text-slate-500 mb-8">
                        The mark is a coral disc with a lowercase “c”. Use the wordmark whenever there’s
                        room; use the mark alone for favicons, avatars or square placements.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Wordmark light */}
                        <div className="card-ecom p-8 flex flex-col items-center justify-center surface-warm relative">
                            <img
                                src="/brand/logo-wordmark.svg"
                                alt="Castline wordmark"
                                className="h-12"
                            />
                            <div className="absolute top-3 right-3">
                                <DownloadButton href="/brand/logo-wordmark.svg" label="wordmark" />
                            </div>
                            <div className="absolute bottom-3 left-3 overline text-slate-400">
                                WORDMARK · LIGHT
                            </div>
                        </div>
                        {/* Wordmark dark */}
                        <div
                            className="card-ecom p-8 flex flex-col items-center justify-center relative"
                            style={{ background: "#0F172A" }}
                        >
                            <img
                                src="/brand/logo-wordmark-dark.svg"
                                alt="Castline wordmark dark"
                                className="h-12"
                            />
                            <div className="absolute top-3 right-3">
                                <DownloadButton
                                    href="/brand/logo-wordmark-dark.svg"
                                    label="wordmark-dark"
                                />
                            </div>
                            <div className="absolute bottom-3 left-3 overline text-slate-400">
                                WORDMARK · DARK
                            </div>
                        </div>
                        {/* Mark */}
                        <div className="card-ecom p-8 flex flex-col items-center justify-center surface-warm relative">
                            <img src="/brand/logo-mark.svg" alt="Mark" className="w-20 h-20" />
                            <div className="absolute top-3 right-3">
                                <DownloadButton href="/brand/logo-mark.svg" label="mark" />
                            </div>
                            <div className="absolute bottom-3 left-3 overline text-slate-400">
                                MARK · CORAL
                            </div>
                        </div>
                        {/* Mark mono dark */}
                        <div
                            className="card-ecom p-8 flex flex-col items-center justify-center relative"
                            style={{ background: "#FAF8F2" }}
                        >
                            <img
                                src="/brand/logo-mark-mono-dark.svg"
                                alt="Mark mono"
                                className="w-20 h-20"
                            />
                            <div className="absolute top-3 right-3">
                                <DownloadButton
                                    href="/brand/logo-mark-mono-dark.svg"
                                    label="mark-mono"
                                />
                            </div>
                            <div className="absolute bottom-3 left-3 overline text-slate-400">
                                MARK · MONO
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 grid md:grid-cols-2 gap-4">
                        <div className="card-ecom p-5">
                            <div className="overline text-slate-400 mb-2">CLEAR SPACE</div>
                            <p className="text-sm text-slate-600 leading-relaxed">
                                Keep at least the height of the mark as breathing room around the
                                logo. Don’t crowd it with copy, icons or imagery.
                            </p>
                        </div>
                        <div className="card-ecom p-5">
                            <div className="overline text-slate-400 mb-2">DON&apos;TS</div>
                            <ul className="text-sm text-slate-600 space-y-1">
                                <li>· Don&apos;t recolour the mark outside coral / mono</li>
                                <li>· Don&apos;t stretch, rotate or outline it</li>
                                <li>· Don&apos;t place coral mark on red/orange backgrounds</li>
                            </ul>
                        </div>
                    </div>
                </section>

                {/* COLORS --------------------------------------------- */}
                <section data-testid="bb-colors">
                    <div className="overline text-coral mb-2">02 · COLOUR</div>
                    <h2 className="font-display text-3xl font-semibold tracking-tight mb-1">
                        Colour palette
                    </h2>
                    <p className="text-slate-500 mb-8">
                        Coral leads. Lime supports. Slate carries the type. Everything else is
                        neutral.
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {COLORS.map((c) => (
                            <div
                                key={c.name}
                                className="card-ecom overflow-hidden"
                                data-testid={`color-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                <div
                                    className="h-24 flex items-end p-3"
                                    style={{ backgroundColor: c.hex, color: c.text }}
                                >
                                    <span className="text-xs font-semibold">{c.name}</span>
                                </div>
                                <div className="p-3 text-xs space-y-1">
                                    <div className="flex items-center justify-between text-slate-700">
                                        <span className="font-mono">{c.hex}</span>
                                        <CopyChip text={c.hex} label={c.name + "-hex"} />
                                    </div>
                                    <div className="text-slate-400 font-mono">rgb({c.rgb})</div>
                                    <div className="text-slate-500 pt-1 leading-snug">{c.role}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* TYPOGRAPHY ----------------------------------------- */}
                <section data-testid="bb-type">
                    <div className="overline text-coral mb-2">03 · TYPOGRAPHY</div>
                    <h2 className="font-display text-3xl font-semibold tracking-tight mb-1">
                        Typography
                    </h2>
                    <p className="text-slate-500 mb-8">
                        Two typefaces. <strong>Bricolage Grotesque</strong> for display, headlines
                        and the wordmark. <strong>Plus Jakarta Sans</strong> for body, UI and email.
                        Both are free on Google Fonts.
                    </p>
                    <div className="card-ecom p-6 mb-6 surface-warm">
                        <div className="flex items-center justify-between mb-4">
                            <div className="font-display text-2xl font-semibold">
                                Bricolage Grotesque
                            </div>
                            <a
                                href="https://fonts.google.com/specimen/Bricolage+Grotesque"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs font-semibold text-coral hover:underline"
                            >
                                Get on Google Fonts →
                            </a>
                        </div>
                        <div className="font-display text-5xl font-semibold leading-tight">
                            Aa Bb Cc — 0123456789
                        </div>
                    </div>
                    <div className="card-ecom p-6 mb-8">
                        <div className="flex items-center justify-between mb-4">
                            <div className="text-2xl font-semibold">Plus Jakarta Sans</div>
                            <a
                                href="https://fonts.google.com/specimen/Plus+Jakarta+Sans"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs font-semibold text-coral hover:underline"
                            >
                                Get on Google Fonts →
                            </a>
                        </div>
                        <div className="text-2xl">Aa Bb Cc — 0123456789</div>
                    </div>
                    <div className="card-ecom overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                                    <th className="text-left px-4 py-3 font-semibold">Tag</th>
                                    <th className="text-left px-4 py-3 font-semibold">Spec</th>
                                    <th className="text-left px-4 py-3 font-semibold">Sample</th>
                                </tr>
                            </thead>
                            <tbody>
                                {TYPESCALE.map((t, i) => (
                                    <tr
                                        key={t.tag}
                                        className={i !== TYPESCALE.length - 1 ? "border-b hairline" : ""}
                                    >
                                        <td className="px-4 py-3 font-semibold text-slate-900 align-top">
                                            {t.tag}
                                        </td>
                                        <td className="px-4 py-3 text-slate-500 font-mono text-xs align-top">
                                            {t.spec}
                                        </td>
                                        <td className="px-4 py-3 align-top">{t.sample}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* VOICE ---------------------------------------------- */}
                <section data-testid="bb-voice">
                    <div className="overline text-coral mb-2">04 · VOICE</div>
                    <h2 className="font-display text-3xl font-semibold tracking-tight mb-1">
                        Voice &amp; messaging
                    </h2>
                    <p className="text-slate-500 mb-8">
                        We talk like a smart operator, not a marketing department.
                    </p>

                    <div className="grid md:grid-cols-2 gap-3 mb-8">
                        <div className="card-ecom p-5">
                            <div className="chip chip-lime inline-flex mb-3">DO</div>
                            <ul className="text-sm text-slate-700 space-y-2">
                                {VOICE.map((v) => (
                                    <li key={v.do} className="leading-relaxed">{v.do}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="card-ecom p-5">
                            <div className="chip chip-coral inline-flex mb-3">DON&apos;T</div>
                            <ul className="text-sm text-slate-700 space-y-2">
                                {VOICE.map((v) => (
                                    <li key={v.dont} className="leading-relaxed">{v.dont}</li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div className="card-ecom overflow-hidden">
                        {KEY_LINES.map((k, i) => (
                            <div
                                key={k.label}
                                className={`p-5 flex items-start justify-between gap-4 ${
                                    i !== KEY_LINES.length - 1 ? "border-b hairline" : ""
                                }`}
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="overline text-slate-400 mb-1">{k.label}</div>
                                    <div className="text-slate-900 leading-relaxed">{k.value}</div>
                                </div>
                                <CopyChip text={k.value} label={k.label.toLowerCase().replace(/\s+/g, "-")} />
                            </div>
                        ))}
                    </div>
                </section>

                {/* EMAIL SIGNATURE ------------------------------------ */}
                <section data-testid="bb-email">
                    <div className="overline text-coral mb-2">05 · EMAIL</div>
                    <h2 className="font-display text-3xl font-semibold tracking-tight mb-1">
                        Email signature
                    </h2>
                    <p className="text-slate-500 mb-8">
                        Drop this into Gmail / Outlook. Replace name, role and link. Coral &amp;
                        slate only — keep it clean.
                    </p>

                    {/* Live preview */}
                    <div className="card-ecom p-6 mb-4">
                        <table cellPadding="0" cellSpacing="0" border="0" style={{ fontFamily: "'Plus Jakarta Sans','Helvetica Neue',Arial,sans-serif" }}>
                            <tbody>
                                <tr>
                                    <td style={{ verticalAlign: "top", paddingRight: 16 }}>
                                        <img src="/brand/logo-mark.svg" alt="Castline" width="44" height="44" />
                                    </td>
                                    <td style={{ verticalAlign: "top", borderLeft: "2px solid #FF5A5F", paddingLeft: 16 }}>
                                        <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A", lineHeight: 1.2 }}>
                                            Your Name
                                        </div>
                                        <div style={{ fontSize: 13, color: "#475569", marginTop: 2 }}>
                                            Founder · Castline
                                        </div>
                                        <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 8 }}>
                                            <a href="https://joincastline.com" style={{ color: "#FF5A5F", textDecoration: "none", fontWeight: 600 }}>
                                                joincastline.com
                                            </a>
                                            &nbsp;·&nbsp;
                                            <a href="mailto:hello@joincastline.com" style={{ color: "#94A3B8", textDecoration: "none" }}>
                                                hello@joincastline.com
                                            </a>
                                        </div>
                                        <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 10, fontStyle: "italic" }}>
                                            Make it once. Get paid every time it performs.
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <details className="card-ecom p-5" data-testid="email-signature-html">
                        <summary className="cursor-pointer text-sm font-semibold text-slate-700">
                            Show HTML to copy
                        </summary>
                        <pre className="mt-4 text-xs bg-slate-50 p-4 rounded-lg overflow-x-auto leading-relaxed">
{`<table cellpadding="0" cellspacing="0" border="0"
       style="font-family:'Plus Jakarta Sans','Helvetica Neue',Arial,sans-serif">
  <tr>
    <td style="vertical-align:top;padding-right:16px">
      <img src="https://joincastline.com/brand/logo-mark.svg"
           alt="Castline" width="44" height="44">
    </td>
    <td style="vertical-align:top;border-left:2px solid #FF5A5F;
               padding-left:16px">
      <div style="font-size:15px;font-weight:700;color:#0F172A;line-height:1.2">
        Your Name
      </div>
      <div style="font-size:13px;color:#475569;margin-top:2px">
        Founder · Castline
      </div>
      <div style="font-size:12px;color:#94A3B8;margin-top:8px">
        <a href="https://joincastline.com"
           style="color:#FF5A5F;text-decoration:none;font-weight:600">
          joincastline.com
        </a> ·
        <a href="mailto:hello@joincastline.com"
           style="color:#94A3B8;text-decoration:none">
          hello@joincastline.com
        </a>
      </div>
      <div style="font-size:11px;color:#94A3B8;margin-top:10px;
                  font-style:italic">
        Make it once. Get paid every time it performs.
      </div>
    </td>
  </tr>
</table>`}
                        </pre>
                    </details>
                </section>

                {/* ASSETS DOWNLOAD ------------------------------------ */}
                <section data-testid="bb-assets">
                    <div className="overline text-coral mb-2">06 · ASSETS</div>
                    <h2 className="font-display text-3xl font-semibold tracking-tight mb-1">
                        Download everything
                    </h2>
                    <p className="text-slate-500 mb-6">
                        SVGs scale anywhere. For a one-off PNG/JPG, open the SVG and export from
                        your design tool.
                    </p>
                    <button
                        onClick={() => {
                            const files = [
                                "/brand/logo-mark.svg",
                                "/brand/logo-mark-mono-dark.svg",
                                "/brand/logo-wordmark.svg",
                                "/brand/logo-wordmark-dark.svg",
                            ];
                            files.forEach((f, i) => {
                                setTimeout(() => {
                                    const a = document.createElement("a");
                                    a.href = f;
                                    a.download = f.split("/").pop();
                                    document.body.appendChild(a);
                                    a.click();
                                    a.remove();
                                }, i * 250);
                            });
                        }}
                        className="btn-pill-primary inline-flex items-center mb-8 print:hidden"
                        data-testid="bb-download-all"
                    >
                        <Download className="w-4 h-4 mr-2" /> Download all logos (4 SVGs)
                    </button>
                    <div className="grid md:grid-cols-2 gap-3">
                        {[
                            ["/brand/logo-mark.svg", "Logo mark · coral", "Square mark for avatars and favicons"],
                            ["/brand/logo-mark-mono-dark.svg", "Logo mark · mono dark", "When coral isn’t available"],
                            ["/brand/logo-wordmark.svg", "Wordmark · light", "For light backgrounds"],
                            ["/brand/logo-wordmark-dark.svg", "Wordmark · dark", "For dark backgrounds"],
                        ].map(([href, name, desc]) => (
                            <a
                                key={href}
                                href={href}
                                download
                                className="card-ecom p-5 flex items-center justify-between hover:shadow-md transition-all group"
                                data-testid={`asset-${name.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                                <div className="min-w-0">
                                    <div className="font-semibold text-slate-900 mb-0.5">{name}</div>
                                    <div className="text-xs text-slate-500">{desc}</div>
                                </div>
                                <Download className="w-5 h-5 text-slate-400 group-hover:text-coral transition-colors flex-shrink-0 ml-3" />
                            </a>
                        ))}
                    </div>
                </section>

                <footer className="border-t hairline pt-8 text-xs text-slate-400 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <img src="/brand/logo-mark.svg" alt="" className="w-5 h-5" />
                        <span>Castline brand book · v1 · April 2026</span>
                    </div>
                    <div>Questions? hello@joincastline.com</div>
                </footer>
            </main>
        </div>
    );
}
